import { useEffect, useLayoutEffect, useState, type Dispatch, type RefObject, type MutableRefObject, type SetStateAction } from "react";

// Auto-condense the xl left-rail options card when, expanded, it would squeeze
// the topic navigator enough that not all topics fit (i.e. it "covers" them).
//
// Behaviour:
//  - On mount and on every window resize, the rail is tentatively expanded and
//    re-measured; if the topic list (the `nav ul` inside the rail) has to
//    scroll, the card is condensed to give the topics room.
//  - It ONLY ever auto-condenses, never auto-expands, so there's no oscillation.
//  - A manual toggle (tracked via `userSetRef`) is respected — once the user
//    opens it themselves it stays open (covering topics is fine) until the next
//    resize, which clears the flag and re-evaluates.
export function useRailAutoCondense(
  railRef: RefObject<HTMLElement>,
  condensed: boolean,
  setCondensed: Dispatch<SetStateAction<boolean>>,
  userSetRef: MutableRefObject<boolean>,
  watch: unknown,
) {
  const [resizeTick, setResizeTick] = useState(0);

  useEffect(() => {
    const onResize = () => {
      userSetRef.current = false; // a resize re-opens the question
      setCondensed(false); // tentatively expand; the layout effect re-checks
      setResizeTick((t) => t + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setCondensed, userSetRef]);

  useLayoutEffect(() => {
    if (userSetRef.current) return; // respect the user's manual choice
    const rail = railRef.current;
    if (!rail) return;
    const list = rail.querySelector("nav ul");
    if (!list) return;
    // Only condense — measuring while expanded; if the topic list overflows its
    // visible area the card is covering topics, so reduce it.
    if (!condensed && list.scrollHeight > list.clientHeight + 2) {
      setCondensed(true);
    }
  }, [condensed, resizeTick, watch, railRef, setCondensed, userSetRef]);
}
